/**
 * Date fixée d'un sondage de dates (spec S3 — Docs/2026-10-01-SPEC-Fixer-la-date-et-annonce-IA.md).
 *
 * Logique pure, sans React ni Supabase : candidats proposés dans la fenêtre
 * « Fixer la date », phrase d'aide à la décision, lecture de la date stockée
 * (`poll_data_json.finalDate`, écrite par la RPC `set_poll_final_date`),
 * annonce de secours quand l'IA échoue ou tarde.
 *
 * Garde-fou de la spec : JAMAIS de noms de votants ici, seulement des chiffres.
 * Aucune fonction de ce module ne reçoit de nom — c'est voulu.
 */
import type { PollInsights, Slot } from "./insights/types";
import { isValidFinalSlot, type FinalSlot } from "./add-to-calendar";

export interface FinalDate extends FinalSlot {
  /** Libellé de groupe (« Week-end du 17-18 oct ») si le créneau en avait un. */
  label?: string;
  /** Annonce relue par l'organisateur (IA ou modèle de secours). */
  announcement?: string;
  yes?: number;
  maybe?: number;
  total?: number;
  /** SEQUENCE du .ics, gérée par la base (+1 quand la date change). */
  sequence?: number;
  fixedAt?: string;
}

export interface FinalCandidate {
  slotId: string;
  date: string;
  endDate?: string;
  start?: string;
  end?: string;
  label?: string;
  yes: number;
  maybe: number;
  no: number;
  total: number;
  /** (oui + ½ peut-être) / participants — même formule que le créneau en tête. */
  score: number;
}

const pad = (n: number) => n.toString().padStart(2, "0");
const toHHMM = (minutes: number) => `${pad(Math.floor(minutes / 60) % 24)}:${pad(minutes % 60)}`;

/** Lit la date fixée d'un sondage, quelle que soit la forme reçue du service. */
export function readFinalDate(poll: unknown): FinalDate | null {
  if (!poll || typeof poll !== "object") return null;
  const p = poll as Record<string, any>;
  const raw =
    p.finalDate ?? p.poll_data_json?.finalDate ?? p.poll_data?.finalDate ?? null;
  if (!isValidFinalSlot(raw)) return null;
  return raw as FinalDate;
}

/** Créneaux proposés, du mieux placé au moins bien placé (égalité : ordre chronologique). */
export function buildFinalCandidates(slots: Slot[], insights: PollInsights): FinalCandidate[] {
  const scoreById = new Map(insights.scores.map((s) => [s.slotId, s]));
  const candidates = slots.map((slot) => {
    const s = scoreById.get(slot.id);
    const total = s?.total ?? insights.participantsCount ?? 0;
    const yes = s?.yes ?? 0;
    const maybe = s?.maybe ?? 0;
    const c: FinalCandidate = {
      slotId: slot.id,
      date: slot.primaryDate,
      yes,
      maybe,
      no: s?.no ?? 0,
      total,
      score: total > 0 ? (yes + maybe * 0.5) / total : 0,
    };
    const dates = (slot.dates ?? []).filter(Boolean).sort();
    if (dates.length > 1) c.endDate = dates[dates.length - 1];
    if (slot.groupLabel) c.label = slot.groupLabel;
    const ts = slot.timeSlots?.[0];
    if (ts && dates.length <= 1) {
      const startMin = ts.hour * 60 + ts.minute;
      c.start = toHHMM(startMin);
      if (ts.duration && ts.duration > 0) c.end = toHHMM(startMin + ts.duration);
    }
    return { c, index: slot.index };
  });
  return candidates
    .sort((a, b) => b.c.score - a.c.score || b.c.yes - a.c.yes || a.index - b.index)
    .map((x) => x.c);
}

/** Candidat à présélectionner : celui du bandeau « créneau en tête » s'il existe. */
export function pickDefaultCandidate(
  candidates: FinalCandidate[],
  winnerSlotId: string | null | undefined,
): FinalCandidate | null {
  if (candidates.length === 0) return null;
  return candidates.find((c) => c.slotId === winnerSlotId) ?? candidates[0];
}

export type DecisionHint =
  | { kind: "none" }
  | { kind: "single"; first: FinalCandidate }
  | { kind: "lead" | "tie"; first: FinalCandidate; second: FinalCandidate };

/**
 * Pourquoi le créneau en tête l'emporte (« Vendredi : 7 dispos, jeudi 6 dont 2
 * peut-être »). Calcul déterministe, pas d'IA : c'est une aide à la décision,
 * elle doit être exacte.
 */
export function buildDecisionHint(candidates: FinalCandidate[]): DecisionHint {
  const voted = candidates.filter((c) => c.total > 0);
  if (voted.length === 0) return { kind: "none" };
  const [first, second] = voted;
  if (!second) return { kind: "single", first };
  const tie = first.yes === second.yes && first.maybe === second.maybe;
  return { kind: tie ? "tie" : "lead", first, second };
}

/** Candidat → valeur envoyée à la RPC (sans annonce ni chiffres). */
export function candidateToFinalSlot(c: FinalCandidate, timezone: string): FinalDate {
  const fd: FinalDate = { date: c.date, timezone };
  if (c.endDate && c.endDate !== c.date) fd.endDate = c.endDate;
  if (c.start) fd.start = c.start;
  if (c.start && c.end) fd.end = c.end;
  if (c.label) fd.label = c.label;
  return fd;
}

/**
 * Titre « propre » pour l'annonce et la carte : contenu utilisateur, donc on
 * retire liens, caractères de contrôle et balises, et on borne la longueur.
 */
export function sanitizeTitle(title: string | null | undefined, max = 80): string {
  const cleaned = (title ?? "")
    .replace(/https?:\/\/\S+/gi, "")
    .replace(/www\.\S+/gi, "")
    .replace(/<[^>]*>/g, "")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (cleaned.length <= max) return cleaned;
  // Coupe à la fin d'un mot quand c'est possible (jamais en plein milieu).
  const cut = cleaned.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > max / 2 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:.-]+$/, "") + "…";
}

/** Emoji d'ambiance déduit du titre, pour l'annonce de secours. */
export function pickEmoji(title: string): string {
  const t = title.toLowerCase();
  const rules: Array<[RegExp, string]> = [
    [/ap[ée]ro|bi[èe]re|beer|drink|cervez|ca[ñn]as|pub|bar\b/, "🍻"],
    [/anniv|birthday|cumple/, "🎂"],
    [/d[îi]ner|d[ée]jeuner|resto|restaurant|dinner|lunch|cena|comida|brunch|pizza|bbq|barbecue/, "🍽️"],
    [/foot|soccer|f[úu]tbol|sport|tennis|padel|basket|run|course|v[ée]lo|rando|hike/, "⚽"],
    [/r[ée]union|meeting|reuni[óo]n|point|call|visio|sync|comit[ée]/, "📌"],
    [/f[êe]te|party|soir[ée]e|fiesta|no[ëe]l|christmas|navidad/, "🎉"],
    [/jeu|game|juego|board|poker/, "🎲"],
    [/film|cin[ée]|movie|pel[íi]cula/, "🎬"],
    [/voyage|trip|week-?end|viaje|vacances/, "🧳"],
  ];
  for (const [re, emoji] of rules) if (re.test(t)) return emoji;
  return "📅";
}

/** Date murale (`YYYY-MM-DD`) en Date UTC midi, pour formater sans décalage. */
function wallDate(date: string): Date {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12));
}

export interface FinalDateLabels {
  /** « jeudi 15 octobre » (ou « samedi 17 – dimanche 18 octobre »). */
  day: string;
  /** « jeudi » — pour « C'est jeudi ! ». */
  weekday: string;
  /** « 15 oct. » */
  dayShort: string;
  /** « 11:30 » / « 11:30 – 12:30 » ; null en journée entière. */
  time: string | null;
}

export function formatFinalDate(fd: FinalSlot, locale: string): FinalDateLabels {
  const d = wallDate(fd.date);
  const fmt = (opts: Intl.DateTimeFormatOptions, date = d) =>
    new Intl.DateTimeFormat(locale, { timeZone: "UTC", ...opts }).format(date);
  let day = fmt({ weekday: "long", day: "numeric", month: "long" });
  if (fd.endDate && fd.endDate !== fd.date) {
    day = `${day} – ${fmt({ weekday: "long", day: "numeric", month: "long" }, wallDate(fd.endDate))}`;
  }
  const time = fd.start ? (fd.end ? `${fd.start} – ${fd.end}` : fd.start) : null;
  return {
    day,
    weekday: fmt({ weekday: "long" }),
    dayShort: fmt({ day: "numeric", month: "short" }),
    time,
  };
}

/** Vrai quand le navigateur n'est pas dans le fuseau de l'organisateur. */
export function differsFromBrowserZone(fd: FinalSlot): boolean {
  if (!fd.timezone || !fd.start) return false;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone !== fd.timezone;
  } catch {
    return false;
  }
}

/** Lien « Faites voter votre groupe → » avec sa source mesurée. */
export function createYourOwnUrl(ref: "final" | "ics", origin = "https://doodates.com"): string {
  return `${origin}/new?ref=${ref}`;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Annonce de secours (IA en échec ou trop lente) — même ton que l'exemple de
 * la spec : « 🍻 C'est vendredi ! Apéro de la promo, 16 oct. à 19:30, 7 sur 8
 * dispos. On se voit là-bas ! ». Chiffres seulement, jamais de noms.
 */
export function buildFallbackAnnouncement(
  t: Translate,
  fd: FinalDate,
  title: string,
  locale: string,
): string {
  const labels = formatFinalDate(fd, locale);
  // Annonce courte : titre borné plus serré que pour l'affichage.
  const clean = sanitizeTitle(title, 60);
  const parts: string[] = [
    t("polls:final_date.fallback.opening", {
      emoji: pickEmoji(clean),
      weekday: labels.weekday,
    }),
  ];
  const when = fd.start
    ? t("polls:final_date.fallback.when_time", { day: labels.dayShort, time: fd.start })
    : t("polls:final_date.fallback.when_day", { day: fd.label || labels.dayShort });
  // « 1 nov. » se termine déjà par un point : pas de « 1 nov.. ».
  const body = clean
    ? t("polls:final_date.fallback.body", { title: clean, when })
    : t("polls:final_date.fallback.body_untitled", { when });
  parts.push(body.replace(/\.\.$/, "."));
  if (typeof fd.yes === "number" && typeof fd.total === "number" && fd.total > 0) {
    parts.push(t("polls:final_date.fallback.count", { count: fd.yes, total: fd.total }));
  }
  parts.push(t("polls:final_date.fallback.closing"));
  return parts.join(" ").replace(/\s+/g, " ").trim();
}
